import { Text } from '@thanx/uikit/text'
import {
  useGetGamingReviewCustomersQuery,
  useGetRecoveryConfigQuery,
  useRestrictCustomerMutation,
} from 'api/recoveryAi'
import type { GamingReviewCustomer } from 'api/recoveryAi.types'
import RestrictCustomerModal from 'components/RestrictCustomerModal'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { useAbility } from 'utilities/ability'
import GamingReviewTable from './GamingReviewTable'
import Pagination from './Pagination'

const t = buildTranslate('recovery_ai.gaming_review')

const PER_PAGE = 25

// Scene-level pane (data fetching lives here, not in the presentational table).
// Lists the suspected-gaming queue and drives review→Restrict via the shared
// components/RestrictCustomerModal (this pane owns the mutation) — the
// CaseDetail in-context restrict path (a single POST that restricts +
// auto-resolves). No whitelist / allow affordance (FR-41). Admin-only Restrict
// is gated on the MerchantSettings CASL ability (mirrors RestrictedCustomers /
// EscalationRulesPane); non-admins are read-only.
const GamingReview = () => {
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')

  const [currentPage, setCurrentPage] = useState(1)
  const [pendingRow, setPendingRow] = useState<GamingReviewCustomer | null>(
    null
  )

  const { data, isLoading, isError, refetch } =
    useGetGamingReviewCustomersQuery({
      page: currentPage,
      per_page: PER_PAGE,
    })

  // AI-303 Unit 7: the merchant's configured auto-resolution limit powers the
  // per-row "N of M" threshold badge. Shares the RTK cache with the other config
  // consumers; undefined (pre-BE / loading) makes the badge show count-only.
  const { data: config } = useGetRecoveryConfigQuery()

  const [restrictCustomer, restrictState] = useRestrictCustomerMutation()

  const rows = data?.data ?? []
  const totalPages = data?.meta.totalPages ?? 1

  // When a successful Restrict (tag invalidation) empties the current page —
  // e.g. the last row on page 3 is restricted — clamp back to the last valid
  // page so the operator is never stranded on an empty page. Guarded on settled
  // data (RTK keeps the prior data during the refetch) so it can't fire mid-load.
  useEffect(() => {
    if (data && currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages))
    }
  }, [data, currentPage, totalPages])

  // Only rows with a source conversation can be restricted here (the modal POSTs
  // by conversation_id); the table disables the action for the rest, so a set
  // pendingRow always has a conversationId — but keep the null-guard for types.
  const pendingConversationId = pendingRow?.conversationId ?? null

  return (
    <div>
      <div className="d-flex flex-column mb-l">
        <Text.Header4 bold color="grey90">
          {t('title')}
        </Text.Header4>
        <Text.BodyText3 color="grey70" className="mt-xs">
          {t('description')}
        </Text.BodyText3>
      </div>

      <GamingReviewTable
        rows={rows}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        canManage={canManage}
        onRestrict={setPendingRow}
        autoResolutionThreshold={config?.auto_resolution_threshold}
      />

      {!isLoading && !isError && rows.length > 0 && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Persistently mounted; isOpen toggles per selected row. On success the
          modal auto-closes and restrictCustomer invalidates RecoveryGamingReview,
          dropping the row from this queue on refetch. */}
      <RestrictCustomerModal
        isOpen={pendingConversationId !== null}
        conversationId={pendingConversationId ?? ''}
        onConfirm={params => restrictCustomer(params).unwrap()}
        isSubmitting={restrictState.isLoading}
        isError={restrictState.isError}
        error={restrictState.error}
        onResetError={restrictState.reset}
        onClose={() => setPendingRow(null)}
      />
    </div>
  )
}

export default GamingReview
